<template>
  <div class="relation-diagram">
    <div class="relation-diagram__tools">
      <button type="button" class="relation-diagram__tool" :aria-label="copy.ZoomOut" @click="zoomBy(-zoomStep)">−</button>
      <span class="relation-diagram__percent">{{ zoomPercent }}%</span>
      <button type="button" class="relation-diagram__tool" :aria-label="copy.ZoomIn" @click="zoomBy(zoomStep)">+</button>
      <button type="button" class="relation-diagram__tool relation-diagram__tool--wide" @click="fit">{{ copy.ZoomFit }}</button>
      <span class="relation-diagram__hint">{{ copy.DiagramHint }}</span>
    </div>

    <div
      ref="scroll"
      class="relation-diagram__scroll"
      :class="{ 'relation-diagram__scroll--panning': panning }"
      @mousedown="startPan"
      @mousemove="movePan"
      @mouseup="endPan"
      @mouseleave="cancelPan"
      @scroll="$emit('close-popover')">
      <div class="relation-diagram__size">
        <div class="relation-diagram__scaled" :style="scaledStyle">
          <div class="relation-diagram__wrap" :style="wrapStyle">
            <svg class="relation-diagram__edges" :width="layout.width" :height="layout.height" aria-hidden="true">
              <path
                v-for="link in links"
                :key="`path-${link.id}`"
                :d="link.path"
                fill="none"
                :stroke="link.stroke"
                :stroke-width="link.width"
                :stroke-dasharray="link.dashed ? '6 4' : null"
                :opacity="link.dim ? 0.3 : 1" />
              <circle
                v-for="link in links"
                :key="`dot-${link.id}`"
                :cx="link.endX"
                :cy="link.endY"
                r="3"
                :fill="link.stroke"
                :opacity="link.dim ? 0.3 : 1" />
            </svg>

            <span
              v-for="link in links"
              :key="`label-${link.id}`"
              class="relation-diagram__edge-label"
              :class="{
                'relation-diagram__edge-label--warn': link.warn,
                'relation-diagram__edge-label--dim': link.dim,
              }"
              :style="{ left: `${link.labelX}px`, top: `${link.labelY}px` }">
              {{ link.label }}
            </span>

            <div
              v-for="node in nodes"
              :key="node.id"
              class="relation-diagram__node"
              :class="{
                'relation-diagram__node--root': node.isRoot,
                'relation-diagram__node--scalar': node.isScalar,
                'relation-diagram__node--warn': node.shiftsGrain,
                'relation-diagram__node--selected': selectedNodeId === node.id || activeNodeId === node.id,
                'relation-diagram__node--dimmed': isDimmed(node),
              }"
              :style="styleOf(node)"
              tabindex="0"
              role="button"
              :aria-pressed="String(selectedNodeId === node.id)"
              :aria-label="node.label"
              @click="focusNode(node)"
              @keydown.enter.prevent="focusNode(node)">
              <div class="relation-diagram__node-line">
                <v-icon x-small :class="node.shiftsGrain ? 'relation-diagram__warn-icon' : 'relation-diagram__node-icon'">
                  {{ node.icon }}
                </v-icon>
                <span class="relation-diagram__node-name">{{ node.label }}</span>
                <span v-if="node.isRoot" class="relation-diagram__node-chip">{{ copy.RootBadge }}</span>
              </div>
              <div class="relation-diagram__node-detail" :title="node.detail">{{ node.detail }}</div>

              <div class="relation-diagram__node-actions">
                <button
                  v-if="node.canConfigure"
                  type="button"
                  class="relation-diagram__node-button"
                  :title="copy.ConfigureRelation"
                  :aria-label="copy.ConfigureRelation"
                  @click.stop="requestConfigure(node, $event)">
                  <v-icon x-small>mdi-tune-variant</v-icon>
                </button>
                <button
                  v-if="node.canRemove"
                  type="button"
                  class="relation-diagram__node-button"
                  :title="copy.RemoveRelation"
                  :aria-label="copy.RemoveRelation"
                  @click.stop="$emit('remove', node.id)">
                  <v-icon x-small>mdi-trash-can-outline</v-icon>
                </button>
              </div>

              <!-- The single add affordance: no ghost suggestions, one green + per node. -->
              <button
                v-if="node.canAdd"
                type="button"
                class="relation-diagram__add"
                :title="copy.AddRelation"
                :aria-label="copy.AddRelation"
                @click.stop="requestAdd(node, $event)">
                <v-icon x-small color="white">mdi-plus</v-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="relation-diagram__legend">
      <span class="relation-diagram__legend-item">
        <span class="relation-diagram__legend-line"></span> {{ copy.LegendPlain }}
      </span>
      <span class="relation-diagram__legend-item">
        <span class="relation-diagram__legend-line relation-diagram__legend-line--warn"></span> {{ copy.LegendFanOut }}
      </span>
    </div>
  </div>
</template>

<script>
  import { log } from '@/utils/debugLogger';
  import { RelationsCopy } from './relationsCopy';
  import { DiagramMetrics, ZoomRange, clampZoom, layoutNodes, linksOf, zoomToFit } from './diagramLayout';

  /** How far a drag has to travel before it counts as a pan rather than a click on the canvas. */
  const PanThreshold = 4;

  /**
   * The definition as a graph: node cards laid out left to right by depth, joined by SVG curves
   * that carry the strategy word. Where things sit is `diagramLayout`'s arithmetic; this component
   * draws it and handles zoom, pan and focus.
   *
   * Selecting a node focuses it rather than editing anything: its own edges brighten and everything
   * else dims, which is how a definition with a dozen relations stays readable.
   *
   * Like the tree, it renders the step's view nodes and mutates nothing.
   */
  export default {
    name: 'RelationDiagram',

    props: {
      /** Every view node in the definition, flat, each carrying its own affordances. */
      nodes: { type: Array, required: true },
      /** The node whose popover is open. */
      activeNodeId: { type: String, default: null },
    },

    data() {
      return {
        copy: RelationsCopy,
        zoomStep: ZoomRange.step,
        zoom: 1,
        selectedNodeId: null,
        panning: false,
        panMoved: false,
        panOrigin: { x: 0, y: 0, left: 0, top: 0 },
      };
    },

    computed: {
      layout() {
        return layoutNodes(this.nodes);
      },

      links() {
        return linksOf(this.nodes, this.layout, this.selectedNodeId);
      },

      zoomPercent() {
        return Math.round(this.zoom * 100);
      },

      /** The drawing at its natural size; the scale is applied on top of it. */
      wrapStyle() {
        return {
          width: `${this.layout.width + DiagramMetrics.canvasPaddingX}px`,
          height: `${this.layout.height + DiagramMetrics.canvasPaddingY}px`,
          transform: `scale(${this.zoom})`,
        };
      },

      /**
       * The room the scaled drawing actually occupies. A transform does not change layout size, so
       * without this box the canvas would never learn it has something to scroll.
       */
      scaledStyle() {
        return {
          width: `${Math.round((this.layout.width + DiagramMetrics.canvasPaddingX) * this.zoom)}px`,
          height: `${Math.round((this.layout.height + DiagramMetrics.canvasPaddingY) * this.zoom)}px`,
        };
      },
    },

    watch: {
      /** Removing a relation cascades: the focused node can be one of the ones that went with it. */
      nodes(current) {
        if (!this.selectedNodeId) return;
        if (current.some((node) => node.id === this.selectedNodeId)) return;

        log('[RelationDiagram] focused node is gone, focus cleared', { nodeId: this.selectedNodeId });
        this.selectedNodeId = null;
      },
    },

    methods: {
      /** The box the layout gave this node. Its width comes from the same metric the curves use. */
      styleOf(node) {
        const position = this.layout.positions[node.id];
        if (!position) return { display: 'none' };
        return {
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${DiagramMetrics.nodeWidth}px`,
          height: `${position.height}px`,
        };
      },

      /** Everything that is neither the focused node nor directly joined to it fades back. */
      isDimmed(node) {
        if (!this.selectedNodeId || this.selectedNodeId === node.id) return false;
        const selected = this.nodes.find((candidate) => candidate.id === this.selectedNodeId);
        if (!selected) return false;
        return selected.parentId !== node.id && node.parentId !== this.selectedNodeId;
      },

      focusNode(node) {
        this.selectedNodeId = this.selectedNodeId === node.id ? null : node.id;
        log('[RelationDiagram] node focused', { nodeId: node.id, focused: this.selectedNodeId });
        this.$emit('close-popover');
      },

      zoomBy(delta) {
        const next = clampZoom(this.zoom + delta);
        log('[RelationDiagram] zoom changed', { from: this.zoom, to: next });
        this.zoom = next;
        this.$emit('close-popover');
      },

      fit() {
        const scroll = this.$refs.scroll;
        if (!scroll) return;

        const next = zoomToFit(this.layout, scroll.clientWidth, scroll.clientHeight);
        log('[RelationDiagram] fit to canvas', { zoom: next });
        this.zoom = next;
        this.$emit('close-popover');
      },

      startPan(event) {
        if (event.target.closest('.relation-diagram__node, button')) return;
        const scroll = this.$refs.scroll;
        if (!scroll) return;

        this.panning = true;
        this.panMoved = false;
        this.panOrigin = {
          x: event.clientX,
          y: event.clientY,
          left: scroll.scrollLeft,
          top: scroll.scrollTop,
        };
        event.preventDefault();
      },

      movePan(event) {
        if (!this.panning) return;
        const scroll = this.$refs.scroll;
        const deltaX = event.clientX - this.panOrigin.x;
        const deltaY = event.clientY - this.panOrigin.y;

        if (Math.abs(deltaX) + Math.abs(deltaY) > PanThreshold) this.panMoved = true;
        scroll.scrollLeft = this.panOrigin.left - deltaX;
        scroll.scrollTop = this.panOrigin.top - deltaY;
      },

      /** A click on empty canvas that did not drag clears the focus — the way a canvas behaves. */
      endPan() {
        if (!this.panning) return;
        this.panning = false;
        if (this.panMoved) return;

        if (this.selectedNodeId) {
          log('[RelationDiagram] focus cleared', { nodeId: this.selectedNodeId });
          this.selectedNodeId = null;
        }
        this.$emit('close-popover');
      },

      cancelPan() {
        this.panning = false;
      },

      requestAdd(node, event) {
        log('[RelationDiagram] add relation requested', { nodeId: node.id });
        this.$emit('add', { nodeId: node.id, rect: event.currentTarget.getBoundingClientRect() });
      },

      requestConfigure(node, event) {
        log('[RelationDiagram] configure relation requested', { nodeId: node.id });
        this.$emit('configure', { nodeId: node.id, rect: event.currentTarget.getBoundingClientRect() });
      },
    },
  };
</script>

<style scoped>
  .relation-diagram__tools {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
  }

  .relation-diagram__tool {
    min-width: 30px;
    height: 30px;
    padding: 0 9px;
    border: 1px solid var(--rel-border-strong, #c7cdcb);
    border-radius: 7px;
    background: var(--rel-surface, #fcfdfc);
    font-weight: 700;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .relation-diagram__tool:hover {
    border-color: var(--rel-primary, #03624c);
    color: var(--rel-primary, #03624c);
  }

  .relation-diagram__tool--wide {
    font-size: 12px;
  }

  .relation-diagram__percent {
    min-width: 38px;
    text-align: center;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .relation-diagram__hint {
    margin-left: 10px;
    font-size: 12.5px;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .relation-diagram__scroll {
    height: 420px;
    overflow: auto;
    border: 1px solid var(--rel-border, #dfe3e1);
    border-radius: 12px;
    background-color: var(--rel-surface, #fcfdfc);
    background-image: radial-gradient(var(--rel-canvas-dot, rgba(3, 98, 76, 0.07)) 1px, transparent 1px);
    background-size: 22px 22px;
    cursor: grab;
    user-select: none;
  }

  .relation-diagram__scroll--panning {
    cursor: grabbing;
  }

  /* Flex + auto margins centre a small graph without clipping a large one when it overflows. */
  .relation-diagram__size {
    display: flex;
    min-width: 100%;
    min-height: 100%;
    padding: 12px;
  }

  .relation-diagram__scaled {
    margin: auto;
  }

  .relation-diagram__wrap {
    position: relative;
    transform-origin: 0 0;
  }

  .relation-diagram__edges {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .relation-diagram__edge-label {
    position: absolute;
    transform: translate(-50%, -50%);
    padding: 1px 8px;
    border: 1px solid var(--rel-border-strong, #c7cdcb);
    border-radius: 999px;
    background: var(--rel-surface, #fcfdfc);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.03em;
    color: var(--rel-ink-muted, #5c6b66);
    pointer-events: none;
  }

  .relation-diagram__edge-label--warn {
    border-color: var(--rel-warn-line, #c77d00);
    color: var(--rel-warn-ink, #8a5600);
  }

  .relation-diagram__edge-label--dim {
    opacity: 0.3;
  }

  .relation-diagram__node {
    position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    padding: 9px 12px;
    border: 1px solid var(--rel-border-strong, #c7cdcb);
    border-radius: 12px;
    background: var(--rel-surface, #fcfdfc);
    cursor: pointer;
  }

  .relation-diagram__node--root {
    border-color: var(--rel-primary, #03624c);
  }

  .relation-diagram__node--scalar {
    border-style: dashed;
    background: var(--rel-surface-sunken, #f6f8f7);
  }

  .relation-diagram__node--warn {
    border-color: var(--rel-warn-line, #c77d00);
  }

  .relation-diagram__node--selected {
    box-shadow: 0 4px 16px rgba(3, 98, 76, 0.22);
  }

  .relation-diagram__node--dimmed {
    opacity: 0.45;
  }

  .relation-diagram__node-line {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
  }

  .relation-diagram__node-icon {
    color: var(--rel-primary, #03624c);
  }

  .relation-diagram__warn-icon {
    color: var(--rel-warn-line, #c77d00);
  }

  .relation-diagram__node-name {
    overflow: hidden;
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--rel-ink, #182420);
  }

  .relation-diagram__node-chip {
    padding: 1px 7px;
    border-radius: 4px;
    background: var(--rel-primary-soft, rgba(3, 98, 76, 0.08));
    font-size: 10.5px;
    font-weight: 600;
    color: var(--rel-primary, #03624c);
  }

  .relation-diagram__node-detail {
    overflow: hidden;
    font-size: 10.5px;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .relation-diagram__node-actions {
    position: absolute;
    top: 6px;
    right: 8px;
    display: flex;
    gap: 3px;
    align-items: center;
    opacity: 0;
    transition: opacity 0.15s;
  }

  .relation-diagram__node:hover .relation-diagram__node-actions,
  .relation-diagram__node:focus-within .relation-diagram__node-actions,
  .relation-diagram__node--selected .relation-diagram__node-actions {
    opacity: 1;
  }

  .relation-diagram__node-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .relation-diagram__node-button:hover {
    background: var(--rel-primary-soft, rgba(3, 98, 76, 0.08));
    color: var(--rel-primary, #03624c);
  }

  .relation-diagram__add {
    position: absolute;
    right: -13px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--rel-primary, #03624c);
    box-shadow: 0 2px 8px rgba(2, 26, 26, 0.25);
  }

  .relation-diagram__legend {
    display: flex;
    gap: 14px;
    align-items: center;
    margin: 10px 2px 0;
    font-size: 12px;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .relation-diagram__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .relation-diagram__legend-line {
    width: 22px;
    height: 0;
    border-top: 1.7px solid #9fb3ad;
  }

  .relation-diagram__legend-line--warn {
    border-top-style: dashed;
    border-top-color: var(--rel-warn-line, #c77d00);
  }
</style>
